import { ChevronDown, Printer } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { CNCVariantI } from '@/redux/cncVariant/cncVariant';
import { useLazyGetCNCVariantsQuery } from '@/redux/cncVariant/cncVariant.api.slice';
import { IPostDetailResponse } from '@/redux/post/post.d';
import MachineCard from '@/components/common/MachineCard';

type Machine = {
  cncVariantId: string;
  cncBrandName: string;
  cncModelName: string;
  cncVariantName: string;
  brandLink?: string;
  modelLink?: string;
  variantLink?: string;
};

const mapVariantToMachine = (
  variantId: string,
  variants: CNCVariantI[]
): Machine => {
  const variant = variants.find((v) => v._id === variantId);
  return {
    cncVariantId: variantId,
    cncBrandName: variant?.brandName || '',
    cncModelName: variant?.modelName || '',
    cncVariantName: variant?.name || '',
    brandLink: variant?.brandLink || '',
    modelLink: variant?.modelLink || '',
    variantLink: variant?.variantLink || '',
  };
};

function OriginalMachineCard({ post }: { post: IPostDetailResponse }) {
  const { originalMachine } = post;
  const [getCNCVariants] = useLazyGetCNCVariantsQuery();
  const [originalMachineData, setOriginalMachineData] = useState<Machine>();

  const getOriginalMachineData = useCallback(async () => {
    const variants = await getCNCVariants({
      variantIdList: [originalMachine],
    }).unwrap();

    setOriginalMachineData(mapVariantToMachine(originalMachine, variants));
  }, [originalMachine, getCNCVariants]);

  useEffect(() => {
    getOriginalMachineData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!originalMachineData) {
    // eslint-disable-next-line react/jsx-no-useless-fragment
    return <></>;
  }

  return (
    <Card className="my-5">
      <CardHeader className="w-full">
        <div className="flex gap-2 items-center justify-between w-full">
          <div className="flex gap-2 items-center w-full">
            <Printer className="h-5 w-5" />
            <CardTitle>Machine Created On</CardTitle>
          </div>
          <ChevronDown className="h-4 w-4 shrink-0 transition-transform duration-200" />
        </div>
      </CardHeader>

      <CardContent className="flex flex-col gap-2">
        <MachineCard
          key={originalMachineData.cncVariantId}
          id={originalMachineData.cncVariantId}
          brand={originalMachineData.cncBrandName}
          model={originalMachineData.cncModelName}
          variant={originalMachineData.cncVariantName}
          brandLink={originalMachineData?.brandLink}
          modelLink={originalMachineData?.modelLink}
          variantLink={originalMachineData?.variantLink}
          type="original"
        />
      </CardContent>
    </Card>
  );
}

export default OriginalMachineCard;
